import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { useProductBrain } from '../../contexts/ProductBrainContext';
import { services } from '../../services/productBrain';
import { entityPath } from '../../lib/productInsights';
import { AsyncAction } from './BrainUI';
import { Collapsible, Pager, usePaged } from '../ui/compact';

export function InsightReferences({ references = [], data }) {
  return <div className="v2-citations">{references.map(ref => {
    const record = data[ref.type]?.find(row => row.id === ref.id);
    if (ref.type === 'workspace') return <Link key="workspace" to="/">Workspace totals</Link>;
    if (!record) return null;
    const path = ref.type === 'imports' ? (record.mapped_id ? entityPath(record.mapped_type, record.mapped_id) : '/onboarding')
      : ref.type === 'time' ? '/time' : entityPath(ref.type, ref.id);
    return <Link key={`${ref.type}:${ref.id}`} to={path}>{record.metadata?.key || record.title || record.activity_title || ref.type}</Link>;
  })}</div>;
}

// Folded by default so AI commentary never pushes a page's own work below the fold.
export default function WorkspaceInsights({ area, origin, limit = 3, pageSize = 3 }) {
  const { data, workspace } = useProductBrain();
  const run = workspace?.onboarding?.analysis;
  const rows = (data.insights || []).filter(row => row.kind === 'insight' && row.status !== 'dismissed' &&
    (!origin || row.origin === origin) && (row.origin === 'autopilot' || !run?.id || row.analysis_id === run.id) && (area === 'command_center' || row.area === area))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, limit);
  const paged = usePaged(rows, pageSize);
  if (!rows.length) return null;
  return <Collapsible id={`insights:${area}:${origin || 'all'}`} className="workspace-insights" aria-label="AI workspace insights"
    icon={Sparkles} title="Insights from your sources" meta={rows.length} summary={rows[0].title}>
    <div className="v2-section-title"><span className="v2-muted">AI analysis · Review the linked evidence when making decisions.</span>
      <Link className="v2-text-action" to={origin === 'autopilot' ? '/okrs' : '/autopilot'}>{origin === 'autopilot' ? 'Review OKRs' : 'Manage agents'}</Link></div>
    {paged.items.map(row => <article className="v2-insight-row" key={row.id}>
      {row.origin === 'autopilot' && <span className="v2-label">Autopilot{row.signal ? ` · ${row.signal}` : ''} · {row.model}</span>}
      <h3>{row.title}</h3><p>{row.detail}</p>{row.next_action && <p><strong>Next step:</strong> {row.next_action}</p>}
      <InsightReferences references={row.references} data={data} />
      <div className="v2-actions"><span className="v2-muted">{new Date(row.created_at).toLocaleDateString()}</span>
        <AsyncAction action={() => services.insights.update(row.id, { status: 'dismissed' })}>Dismiss insight</AsyncAction></div>
    </article>)}
    <div className="v2-pager-row"><Pager paged={paged} label="Insight pages"/></div>
  </Collapsible>;
}
